import { useLocalStorage } from '@vueuse/core';
import { useConvexMutation, useConvexQuery } from 'convex-vue';
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch, type Ref } from 'vue';
import type JitsiConference from '@solyd/lib-jitsi-meet/dist/esm/JitsiConference';
import type JitsiConnection from '@solyd/lib-jitsi-meet/dist/esm/JitsiConnection';
import type JitsiParticipant from '@solyd/lib-jitsi-meet/dist/esm/JitsiParticipant';
import type JitsiLocalTrack from '@solyd/lib-jitsi-meet/dist/esm/modules/RTC/JitsiLocalTrack';
import type JitsiTrack from '@solyd/lib-jitsi-meet/dist/esm/modules/RTC/JitsiTrack';
import type JibriSession from '@solyd/lib-jitsi-meet/dist/esm/modules/recording/JibriSession';
import JitsiMeetJS from '@solyd/lib-jitsi-meet';
import { api } from '../../convex/_generated/api';
import { isRecorderMode, readRecorderXmppCredentials, readStoredToken } from '../convex/auth';
import type { ConferenceState } from '../convex/useConference';
import { buildJitsiConnectionOptions, buildJitsiRoomName, initializeJitsiLibrary, jitsiConferenceOptions } from './config';

type DeviceKind = 'audioinput' | 'videoinput' | 'audiooutput';

export type MediaDeviceOption = {
  label: string;
  value: string;
};

type TrackIntent = 'audio' | 'camera' | 'screenshare';
type RecordingCommand = 'start' | 'stop';
type RecordingStateValue = 'off' | 'pending' | 'on' | 'error';

type JitsiRenderableTrack = JitsiTrack | JitsiLocalTrack;

const CONVEX_PARTICIPANT_ID_PROPERTY = 'convexParticipantId';
const MEDIA_RUNTIME_DEBUG_MAX_EVENTS = 400;
const MEDIA_RUNTIME_DEBUG_QUERY_PARAM = 'debugMediaRuntime';
const SPEAKING_AUDIO_LEVEL_THRESHOLD = 0.05;
const SPEAKING_PULSE_HOLD_MS = 250;

export type MediaRenderableTrack = {
  id: string;
  isLocal: boolean;
  kind: 'audio' | 'video' | 'screenshare';
  label: string;
  mirrored: boolean;
  muted: boolean;
  track: JitsiRenderableTrack;
};

export type RecordingRuntimeState = {
  canControl: boolean;
  desiredState: 'off' | 'on';
  isRecording: boolean;
  session: null | {
    createdAt: number;
    errorMessage: string | null;
    id: string | null;
    provider: 'jitsi';
    providerSessionId: string | null;
    roomName: string | null;
    roomSlug: string | null;
    startedAt: number | null;
    startedByDisplayName: string | null;
    state: RecordingStateValue;
    stoppedAt: number | null;
    stoppedByDisplayName: string | null;
  };
  state: RecordingStateValue;
  viewerOwnsTransitionSync: boolean;
};

const fallbackDeviceOptions: Record<DeviceKind, MediaDeviceOption[]> = {
  audioinput: [{ label: 'Default microphone', value: 'fallback-mic-default' }],
  audiooutput: [{ label: 'Default speakers', value: 'fallback-speaker-default' }],
  videoinput: [{ label: 'Default camera', value: 'fallback-camera-default' }],
};

function formatDeviceLabel(kind: DeviceKind, index: number): string {
  if (kind === 'audioinput') {
    return `Microphone ${index + 1}`;
  }

  if (kind === 'videoinput') {
    return `Camera ${index + 1}`;
  }

  return `Speaker ${index + 1}`;
}

function syncSelection(selectedValue: Ref<string>, options: readonly MediaDeviceOption[], fallbackValue: string): void {
  if (options.length === 0) {
    selectedValue.value = fallbackValue;
    return;
  }

  if (!options.some((option) => option.value === selectedValue.value)) {
    selectedValue.value = options[0]?.value ?? fallbackValue;
  }
}

function resolveDeviceLabel(options: readonly MediaDeviceOption[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? 'Unknown device';
}

function isPermissionDeniedError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError';
  }

  return error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError');
}

function isConstraintError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError';
  }

  return error instanceof Error && /constraints? could not be satisfied|overconstrained/i.test(error.message);
}

function describeTrackStartupError(intent: TrackIntent, error: unknown): string {
  if (isPermissionDeniedError(error)) {
    if (intent === 'screenshare') {
      return 'Browser access to screen sharing is blocked. Allow it for this site and try again.';
    }

    return `Browser access to the ${intent === 'audio' ? 'microphone' : 'camera'} is blocked. Allow it for this site and try again.`;
  }

  if (isConstraintError(error)) {
    if (intent === 'screenshare') {
      return 'Screen sharing is unavailable right now. Try again.';
    }

    return `The selected ${intent === 'audio' ? 'microphone' : 'camera'} is unavailable. Try the default device instead.`;
  }

  return error instanceof Error ? error.message : `Unable to start ${intent}.`;
}

function describeConnectionFailure(details: string): string {
  if (/host-unknown/i.test(details)) {
    return 'Unable to connect media services. The configured Jitsi host could not be resolved.';
  }

  return details ? `Unable to connect media services. ${details}` : 'Unable to connect media services.';
}

function normalizeRecordingState(session: JibriSession | null | undefined): RecordingStateValue {
  if (!session) {
    return 'off';
  }

  const fallbackState = (session as JibriSession & { _statusFromJicofo?: string })._statusFromJicofo;
  const nextState = fallbackState ?? session.getStatus();

  if (nextState === 'off' || nextState === 'pending' || nextState === 'on' || nextState === 'error') {
    return nextState;
  }

  return session.getError() ? 'error' : 'off';
}

function describeRecordingFailure(error: unknown, verb: RecordingCommand): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return `Unable to ${verb} recording.`;
}

function getTrackIdentity(track: JitsiRenderableTrack): string {
  return track.getTrackId() ?? track.getId() ?? `${track.getType()}-${track.getTrackLabel()}`;
}

function isTrackMuted(track: JitsiRenderableTrack): boolean {
  if ('isMuted' in track && typeof track.isMuted === 'function') {
    return track.isMuted();
  }

  return track.isWebRTCTrackMuted();
}

function isMediaRuntimeDebugEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (window.__enableConferenceMediaRuntimeDebug) {
    return true;
  }

  return new URL(window.location.href).searchParams.get(MEDIA_RUNTIME_DEBUG_QUERY_PARAM) === '1';
}

function getTrackDebugDetail(track: JitsiRenderableTrack): Record<string, unknown> {
  return {
    id: getTrackIdentity(track),
    isLocal: track.isLocal(),
    muted: isTrackMuted(track),
    participantId: 'getParticipantId' in track ? track.getParticipantId() : null,
    type: track.getType(),
    videoType: track.getVideoType(),
  };
}

function appendMediaRuntimeDebugEvent(event: string, detail: Record<string, unknown> = {}): void {
  if (!isMediaRuntimeDebugEnabled() || typeof window === 'undefined') {
    return;
  }

  const currentState = window.__conferenceMediaRuntimeDebug ?? {
    enabled: true,
    events: [],
  };
  const nextSequence = currentState.events.length > 0 ? currentState.events[currentState.events.length - 1].sequence + 1 : 1;
  const nextEvent: ConferenceMediaRuntimeDebugEvent = {
    at: Date.now(),
    detail,
    event,
    sequence: nextSequence,
  };

  window.__conferenceMediaRuntimeDebug = {
    enabled: true,
    events: [...currentState.events.slice(-(MEDIA_RUNTIME_DEBUG_MAX_EVENTS - 1)), nextEvent],
  };
}

function buildTrackView(track: JitsiRenderableTrack, index: number, dominantSpeakerId: string | null): MediaRenderableTrack {
  const type = track.getType();
  const videoType = track.getVideoType();
  const isVideoTrack = type === 'video';
  const isScreenshareTrack = isVideoTrack && videoType === 'desktop';
  const participantId = 'getParticipantId' in track ? track.getParticipantId() : null;
  const isDominant = Boolean(!track.isLocal() && participantId && dominantSpeakerId && participantId === dominantSpeakerId);

  return {
    id: getTrackIdentity(track),
    isLocal: track.isLocal(),
    kind: isScreenshareTrack ? 'screenshare' : isVideoTrack ? 'video' : 'audio',
    label: track.isLocal()
      ? 'You'
      : isScreenshareTrack
        ? `Screen share ${index + 1}`
        : isDominant
          ? 'Active speaker'
          : `Participant ${index + 1}`,
    mirrored: track.isLocal() && isVideoTrack && !isScreenshareTrack,
    muted: track.isLocal() || isTrackMuted(track) || type === 'video',
    track,
  };
}

function removeTrackFromCollection(collection: MediaRenderableTrack[], trackId: string): MediaRenderableTrack[] {
  return collection.filter((entry) => entry.id !== trackId);
}

export function useConferenceMediaRuntime(conferenceSlug: Ref<string>, conferenceState: Ref<ConferenceState>) {
  const { mutate: setTrackState } = useConvexMutation(api.participants.setTrackState);
  const { mutate: requestStartRecording } = useConvexMutation(api.recording.requestStartRecording);
  const { mutate: requestStopRecording } = useConvexMutation(api.recording.requestStopRecording);
  const { mutate: syncClientRecordingState } = useConvexMutation(api.recording.syncClientRecordingState);
  const { mutate: markControllerLost } = useConvexMutation(api.recording.markControllerLost);
  const recordingQueryArgs = computed(() => ({
    conferenceSlug: conferenceSlug.value,
  }));
  const {
    data: recordingStateData,
    error: recordingQueryError,
    isPending: isRecordingStatePending,
  } = useConvexQuery(api.recording.getState, recordingQueryArgs);

  const audioInputOptions = ref<MediaDeviceOption[]>(fallbackDeviceOptions.audioinput);
  const cameraOptions = ref<MediaDeviceOption[]>(fallbackDeviceOptions.videoinput);
  const audioOutputOptions = ref<MediaDeviceOption[]>(fallbackDeviceOptions.audiooutput);

  const selectedAudioInput = useLocalStorage('convex-rebuild.preferences.audio-input', fallbackDeviceOptions.audioinput[0].value);
  const selectedCamera = useLocalStorage('convex-rebuild.preferences.camera-input', fallbackDeviceOptions.videoinput[0].value);
  const selectedAudioOutput = useLocalStorage(
    'convex-rebuild.preferences.audio-output',
    fallbackDeviceOptions.audiooutput[0].value,
  );
  const videoPanelVisible = useLocalStorage('convex-rebuild.preferences.video-panel-visible', false);
  const videoPanelLayout = useLocalStorage<'floating' | 'docked'>('convex-rebuild.preferences.video-panel-layout', 'floating');
  const selfVideoEnabled = useLocalStorage('convex-rebuild.preferences.self-video', true);

  const browserPermissionDenied = ref({
    audio: false,
    camera: false,
  });
  const runtimeError = ref<string | null>(null);
  const runtimeStatus = ref('Preparing media runtime.');
  const recordingError = ref<string | null>(null);
  const recordingCommandInFlight = ref<RecordingCommand | null>(null);
  const dominantSpeakerId = ref<string | null>(null);
  const speakingParticipantIds = ref<ConferenceState['participants'][number]['id'][]>([]);
  const activeRecordingSession = shallowRef<JibriSession | null>(null);

  const localTracks = ref<MediaRenderableTrack[]>([]);
  const remoteTracks = ref<MediaRenderableTrack[]>([]);
  const localTrackStateOverrides = ref<Partial<ConferenceState['viewer']['trackState']>>({});

  const connection = shallowRef<JitsiConnection | null>(null);
  const conference = shallowRef<JitsiConference | null>(null);
  const connectionReady = ref(false);
  const activeRoomName = ref<string | null>(null);

  let connectionPromise: Promise<void> | null = null;
  let syncQueue: Promise<void> = Promise.resolve();
  let roomQueue: Promise<void> = Promise.resolve();
  const conferenceParticipantIdByJitsiParticipantId = new Map<string, ConferenceState['participants'][number]['id']>();
  const recentSpeakingActivityByJitsiParticipantId = new Map<string, number>();
  const speakingResetTimers = new Map<ConferenceState['participants'][number]['id'], number>();
  const trackEventBindings = new WeakSet<JitsiRenderableTrack>();
  const disposingTracks = new WeakSet<JitsiRenderableTrack>();
  const deniedTrackActivationIntents = new Set<TrackIntent>();
  const desiredViewerTrackState = computed(() => ({
    ...conferenceState.value.viewer.trackState,
    ...localTrackStateOverrides.value,
  }));

  function setStatus(message: string): void {
    runtimeStatus.value = message;
  }

  function rememberRuntimeError(message: string): void {
    runtimeError.value = message;
    setStatus(message);
  }

  function clearRuntimeError(): void {
    runtimeError.value = null;
  }

  function clearRecordingError(): void {
    recordingError.value = null;
  }

  const recordingState = computed(() => (recordingStateData.value as RecordingRuntimeState | null) ?? null);
  const shouldBlockUnload = computed(
    () => recordingCommandInFlight.value !== null || (recordingState.value?.state ?? 'off') !== 'off',
  );
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!shouldBlockUnload.value) {
      return undefined;
    }

    event.preventDefault();
    event.returnValue = 'Please stop recording before closing this tab.';
    return event.returnValue;
  };

  function setSpeakingState(participantId: ConferenceState['participants'][number]['id'], speaking: boolean): void {
    const activeSpeakerIds = new Set(speakingParticipantIds.value);

    if (speaking) {
      activeSpeakerIds.add(participantId);
      speakingParticipantIds.value = [...activeSpeakerIds];

      const existingTimer = speakingResetTimers.get(participantId);

      if (existingTimer) {
        window.clearTimeout(existingTimer);
      }

      const timeoutHandle = window.setTimeout(() => {
        speakingResetTimers.delete(participantId);
        setSpeakingState(participantId, false);
      }, SPEAKING_PULSE_HOLD_MS);

      speakingResetTimers.set(participantId, timeoutHandle);
      return;
    }

    const existingTimer = speakingResetTimers.get(participantId);

    if (existingTimer) {
      window.clearTimeout(existingTimer);
      speakingResetTimers.delete(participantId);
    }

    if (activeSpeakerIds.delete(participantId)) {
      speakingParticipantIds.value = [...activeSpeakerIds];
    }
  }

  function resetSpeakingState(): void {
    for (const timeoutHandle of speakingResetTimers.values()) {
      window.clearTimeout(timeoutHandle);
    }

    speakingResetTimers.clear();
    recentSpeakingActivityByJitsiParticipantId.clear();
    conferenceParticipantIdByJitsiParticipantId.clear();
    speakingParticipantIds.value = [];
  }

  function rememberRecentSpeakingActivity(jitsiParticipantId: string): void {
    recentSpeakingActivityByJitsiParticipantId.set(jitsiParticipantId, Date.now());
  }

  function flushRecentSpeakingActivity(
    jitsiParticipantId: string,
    conferenceParticipantId: ConferenceState['participants'][number]['id'],
  ): void {
    const lastSpokeAt = recentSpeakingActivityByJitsiParticipantId.get(jitsiParticipantId);

    if (lastSpokeAt === undefined) {
      return;
    }

    recentSpeakingActivityByJitsiParticipantId.delete(jitsiParticipantId);

    if (Date.now() - lastSpokeAt <= SPEAKING_PULSE_HOLD_MS) {
      setSpeakingState(conferenceParticipantId, true);
    }
  }

  function mapJitsiParticipantToConferenceParticipant(
    jitsiParticipantId: string,
    conferenceParticipantId: ConferenceState['participants'][number]['id'],
  ): void {
    conferenceParticipantIdByJitsiParticipantId.set(jitsiParticipantId, conferenceParticipantId);
    flushRecentSpeakingActivity(jitsiParticipantId, conferenceParticipantId);
  }

  function registerParticipantMappingFromParticipant(participant: JitsiParticipant | null | undefined): void {
    if (!participant) {
      return;
    }

    const conferenceParticipantId = participant.getProperty(CONVEX_PARTICIPANT_ID_PROPERTY) as
      | ConferenceState['participants'][number]['id']
      | undefined;

    if (!conferenceParticipantId) {
      return;
    }

    mapJitsiParticipantToConferenceParticipant(participant.getId(), conferenceParticipantId);
  }

  function resolveConferenceParticipantId(jitsiParticipantId: string): ConferenceState['participants'][number]['id'] | null {
    const mappedParticipantId = conferenceParticipantIdByJitsiParticipantId.get(jitsiParticipantId);

    if (mappedParticipantId) {
      return mappedParticipantId;
    }

    if (conference.value?.myUserId() === jitsiParticipantId) {
      const viewerParticipantId = conferenceState.value.viewer.id;
      mapJitsiParticipantToConferenceParticipant(jitsiParticipantId, viewerParticipantId);
      return viewerParticipantId;
    }

    registerParticipantMappingFromParticipant(conference.value?.getParticipantById(jitsiParticipantId));

    return conferenceParticipantIdByJitsiParticipantId.get(jitsiParticipantId) ?? null;
  }

  function resolveJitsiParticipantIdForTrack(track: JitsiRenderableTrack): string | null {
    if (track.isLocal()) {
      return conference.value?.myUserId() ?? null;
    }

    return 'getParticipantId' in track ? track.getParticipantId() : null;
  }

  function handleTrackAudioLevelChanged(jitsiParticipantId: string, audioLevel: number): void {
    if (audioLevel <= SPEAKING_AUDIO_LEVEL_THRESHOLD) {
      return;
    }

    const conferenceParticipantId = resolveConferenceParticipantId(jitsiParticipantId);

    if (!conferenceParticipantId) {
      rememberRecentSpeakingActivity(jitsiParticipantId);
      appendMediaRuntimeDebugEvent('speaking-participant-unresolved', {
        audioLevel,
        jitsiParticipantId,
      });
      return;
    }

    setSpeakingState(conferenceParticipantId, true);
  }

  function clearSpeakingStateForTrack(track: JitsiRenderableTrack): void {
    const jitsiParticipantId = resolveJitsiParticipantIdForTrack(track);

    if (!jitsiParticipantId) {
      return;
    }

    const conferenceParticipantId = resolveConferenceParticipantId(jitsiParticipantId);

    if (conferenceParticipantId) {
      setSpeakingState(conferenceParticipantId, false);
    }
  }

  function handleParticipantLeft(jitsiParticipantId: string): void {
    const conferenceParticipantId = conferenceParticipantIdByJitsiParticipantId.get(jitsiParticipantId);

    if (conferenceParticipantId) {
      setSpeakingState(conferenceParticipantId, false);
    }

    recentSpeakingActivityByJitsiParticipantId.delete(jitsiParticipantId);
    conferenceParticipantIdByJitsiParticipantId.delete(jitsiParticipantId);
  }

  async function syncProviderRecordingState(session: JibriSession, explicitStatus?: RecordingStateValue): Promise<void> {
    if (!recordingState.value?.viewerOwnsTransitionSync && recordingCommandInFlight.value === null) {
      return;
    }

    const status = explicitStatus ?? normalizeRecordingState(session);

    await syncClientRecordingState({
      conferenceSlug: conferenceSlug.value,
      errorMessage: session.getError() ?? undefined,
      providerSessionId: session.getID(),
      status,
    });
  }

  async function handleControllerDeparture(reason: string): Promise<void> {
    if (!recordingState.value?.viewerOwnsTransitionSync || recordingState.value.state !== 'pending') {
      return;
    }

    try {
      await markControllerLost({
        conferenceSlug: conferenceSlug.value,
        errorMessage: reason,
      });
    } catch {
      // Ignore teardown-time controller loss errors to avoid masking the primary disconnect path.
    }
  }

  async function startRecording(): Promise<void> {
    if (!conference.value) {
      throw new Error('Recording is unavailable until the media room is connected.');
    }

    clearRecordingError();
    recordingCommandInFlight.value = 'start';

    try {
      await requestStartRecording({
        conferenceSlug: conferenceSlug.value,
      });

      const session = (await conference.value.startRecording({
        mode: JitsiMeetJS.constants.recording.mode.FILE,
      })) as JibriSession;

      activeRecordingSession.value = session;
      await syncProviderRecordingState(session, normalizeRecordingState(session));
    } catch (error) {
      const message = describeRecordingFailure(error, 'start');
      recordingError.value = message;

      try {
        await syncClientRecordingState({
          conferenceSlug: conferenceSlug.value,
          errorMessage: message,
          providerSessionId:
            activeRecordingSession.value?.getID() ?? recordingState.value?.session?.providerSessionId ?? undefined,
          status: 'error',
        });
      } catch {
        // Ignore sync follow-up failures so the user still sees the original provider error.
      }

      throw new Error(message);
    } finally {
      recordingCommandInFlight.value = null;
    }
  }

  async function stopRecording(): Promise<void> {
    if (!conference.value) {
      throw new Error('Recording is unavailable until the media room is connected.');
    }

    clearRecordingError();
    recordingCommandInFlight.value = 'stop';

    try {
      await requestStopRecording({
        conferenceSlug: conferenceSlug.value,
      });

      const providerSessionId = activeRecordingSession.value?.getID() ?? recordingState.value?.session?.providerSessionId;

      if (!providerSessionId) {
        throw new Error('The active Jitsi recording session could not be identified.');
      }

      await conference.value.stopRecording(providerSessionId);
    } catch (error) {
      const message = describeRecordingFailure(error, 'stop');
      recordingError.value = message;

      try {
        await syncClientRecordingState({
          conferenceSlug: conferenceSlug.value,
          errorMessage: message,
          providerSessionId:
            activeRecordingSession.value?.getID() ?? recordingState.value?.session?.providerSessionId ?? undefined,
          status: 'error',
        });
      } catch {
        // Ignore sync follow-up failures so the user still sees the original provider error.
      }

      throw new Error(message);
    } finally {
      recordingCommandInFlight.value = null;
    }
  }

  async function toggleRecording(): Promise<void> {
    if (recordingState.value?.state === 'on') {
      await stopRecording();
      return;
    }

    await startRecording();
  }

  async function loadDeviceOptions(): Promise<void> {
    if (!navigator.mediaDevices?.enumerateDevices) {
      audioInputOptions.value = fallbackDeviceOptions.audioinput;
      cameraOptions.value = fallbackDeviceOptions.videoinput;
      audioOutputOptions.value = fallbackDeviceOptions.audiooutput;
      return;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();

    const nextAudioInputs = devices
      .filter((device) => device.kind === 'audioinput')
      .map((device, index) => ({
        label: device.label || formatDeviceLabel('audioinput', index),
        value: device.deviceId || `audioinput-${index}`,
      }));
    const nextVideoInputs = devices
      .filter((device) => device.kind === 'videoinput')
      .map((device, index) => ({
        label: device.label || formatDeviceLabel('videoinput', index),
        value: device.deviceId || `videoinput-${index}`,
      }));
    const nextAudioOutputs = devices
      .filter((device) => device.kind === 'audiooutput')
      .map((device, index) => ({
        label: device.label || formatDeviceLabel('audiooutput', index),
        value: device.deviceId || `audiooutput-${index}`,
      }));

    audioInputOptions.value = nextAudioInputs.length > 0 ? nextAudioInputs : fallbackDeviceOptions.audioinput;
    cameraOptions.value = nextVideoInputs.length > 0 ? nextVideoInputs : fallbackDeviceOptions.videoinput;
    audioOutputOptions.value = nextAudioOutputs.length > 0 ? nextAudioOutputs : fallbackDeviceOptions.audiooutput;
  }

  async function applyAudioOutputSelection(): Promise<void> {
    if (!selectedAudioOutput.value.startsWith('fallback-')) {
      await JitsiMeetJS.mediaDevices.setAudioOutputDevice(selectedAudioOutput.value);
    }
  }

  function resetRenderedTracks(): void {
    localTracks.value = [];
    remoteTracks.value = [];
  }

  function bindTrackCollectionEvents(track: JitsiRenderableTrack): void {
    if (trackEventBindings.has(track)) {
      return;
    }

    trackEventBindings.add(track);

    track.addEventListener(JitsiMeetJS.events.track.TRACK_MUTE_CHANGED, () => {
      if (!isRenderedTrack(track)) {
        return;
      }

      appendMediaRuntimeDebugEvent('track-mute-changed', getTrackDebugDetail(track));

      if (track.getType() === 'audio' && isTrackMuted(track)) {
        clearSpeakingStateForTrack(track);
      }

      updateTrackCollections(track);
    });

    if (track.getType() === 'audio') {
      track.addEventListener(JitsiMeetJS.events.track.TRACK_AUDIO_LEVEL_CHANGED, ((audioLevel: number) => {
        if (!isRenderedTrack(track)) {
          return;
        }

        const participantId = resolveJitsiParticipantIdForTrack(track);

        if (!participantId) {
          return;
        }

        handleTrackAudioLevelChanged(participantId, audioLevel);
      }) as () => unknown);
    }

    if (track.getType() === 'video') {
      track.addEventListener(JitsiMeetJS.events.track.TRACK_VIDEOTYPE_CHANGED, () => {
        if (!isRenderedTrack(track)) {
          return;
        }

        appendMediaRuntimeDebugEvent('track-video-type-changed', getTrackDebugDetail(track));
        updateTrackCollections(track);
      });
    }
  }

  function updateTrackCollections(track: JitsiRenderableTrack): void {
    const targetCollection = track.isLocal() ? localTracks : remoteTracks;
    const nextCollection = removeTrackFromCollection(targetCollection.value, getTrackIdentity(track));
    const trackView = buildTrackView(track, nextCollection.length, dominantSpeakerId.value);

    bindTrackCollectionEvents(track);

    targetCollection.value = [...nextCollection, trackView];
    appendMediaRuntimeDebugEvent('update-track-collections', {
      localTrackCount: localTracks.value.length,
      remoteTrackCount: remoteTracks.value.length,
      track: getTrackDebugDetail(track),
      trackKind: trackView.kind,
    });
  }

  function removeTrack(track: JitsiRenderableTrack): void {
    const trackId = getTrackIdentity(track);
    localTracks.value = removeTrackFromCollection(localTracks.value, trackId);
    remoteTracks.value = removeTrackFromCollection(remoteTracks.value, trackId);
    appendMediaRuntimeDebugEvent('remove-track', {
      localTrackCount: localTracks.value.length,
      remoteTrackCount: remoteTracks.value.length,
      track: getTrackDebugDetail(track),
    });
  }

  function isRenderedTrack(track: JitsiRenderableTrack): boolean {
    const trackId = getTrackIdentity(track);

    return [...localTracks.value, ...remoteTracks.value].some((entry) => entry.track === track || entry.id === trackId);
  }

  async function disposeTrack(track: JitsiRenderableTrack): Promise<void> {
    if (disposingTracks.has(track)) {
      return;
    }

    disposingTracks.add(track);
    appendMediaRuntimeDebugEvent('dispose-track-start', getTrackDebugDetail(track));

    try {
      if (conference.value && track.isLocal()) {
        await conference.value.removeTrack(track as JitsiLocalTrack);
      }

      track.dispose();
    } finally {
      removeTrack(track);
      disposingTracks.delete(track);
    }
  }

  async function stopLocalScreenshareTrack(track: JitsiLocalTrack): Promise<void> {
    if (!disposingTracks.has(track)) {
      await disposeTrack(track);
    }

    if (conferenceState.value.viewer.trackState.screenshare) {
      await syncTrackState('screenshare', false);
    }
  }

  async function ensureLocalTrackPublished(track: JitsiLocalTrack): Promise<void> {
    if (!conference.value) {
      return;
    }

    const publishedTracks = [conference.value.getLocalAudioTrack(), conference.value.getLocalVideoTrack()].filter(
      (entry): entry is JitsiLocalTrack => entry !== null,
    );

    if (publishedTracks.includes(track)) {
      appendMediaRuntimeDebugEvent('ensure-local-track-published:already-published', getTrackDebugDetail(track));
      return;
    }

    await conference.value.addTrack(track);
    appendMediaRuntimeDebugEvent('ensure-local-track-published:added', getTrackDebugDetail(track));
    updateTrackCollections(track);
  }

  async function leaveConference(): Promise<void> {
    if (!conference.value) {
      return;
    }

    await handleControllerDeparture('Recording controller left the media room before recording was reconciled.');

    const currentConference = conference.value;
    conference.value = null;
    activeRoomName.value = null;
    dominantSpeakerId.value = null;
    activeRecordingSession.value = null;
    resetSpeakingState();

    for (const localTrack of [...localTracks.value]) {
      await disposeTrack(localTrack.track);
    }

    await currentConference.leave();
    resetRenderedTracks();
  }

  async function disconnect(): Promise<void> {
    await leaveConference();

    if (connection.value) {
      const currentConnection = connection.value;
      connection.value = null;
      connectionReady.value = false;
      await currentConnection.disconnect();
    }
  }

  async function ensureConnection(): Promise<void> {
    if (connectionReady.value && connection.value) {
      return;
    }

    if (connectionPromise) {
      return connectionPromise;
    }

    initializeJitsiLibrary();
    const recorderCredentials = isRecorderMode() ? readRecorderXmppCredentials(window.localStorage) : null;
    const inviteToken = recorderCredentials ? null : readStoredToken(window.sessionStorage);
    const jitsiConnection = new JitsiMeetJS.JitsiConnection(
      undefined,
      inviteToken ?? undefined,
      buildJitsiConnectionOptions(conferenceSlug.value, inviteToken),
    );

    connection.value = jitsiConnection;
    setStatus('Connecting media services.');
    appendMediaRuntimeDebugEvent('ensure-connection:start', {
      conferenceSlug: conferenceSlug.value,
    });

    connectionPromise = new Promise<void>((resolve, reject) => {
      const handleConnected = () => {
        connectionReady.value = true;
        clearRuntimeError();
        setStatus('Media services connected.');
        appendMediaRuntimeDebugEvent('ensure-connection:connected', {
          conferenceSlug: conferenceSlug.value,
        });
        jitsiConnection.removeEventListener(JitsiMeetJS.events.connection.CONNECTION_FAILED, handleFailed);
        jitsiConnection.removeEventListener(JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED, handleDisconnected);
        resolve();
      };

      const handleFailed = (...args: unknown[]) => {
        const details = args
          .map((entry) => (entry instanceof Error ? entry.message : typeof entry === 'string' ? entry : ''))
          .filter(Boolean)
          .join(' ');
        const message = describeConnectionFailure(details);
        rememberRuntimeError(message);
        appendMediaRuntimeDebugEvent('ensure-connection:failed', {
          conferenceSlug: conferenceSlug.value,
          message,
        });
        jitsiConnection.removeEventListener(JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED, handleConnected);
        jitsiConnection.removeEventListener(JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED, handleDisconnected);
        reject(new Error(message));
      };

      const handleDisconnected = () => {
        connectionReady.value = false;
        setStatus('Media services disconnected.');
        appendMediaRuntimeDebugEvent('ensure-connection:disconnected', {
          conferenceSlug: conferenceSlug.value,
        });
      };

      jitsiConnection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED, handleConnected);
      jitsiConnection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_FAILED, handleFailed);
      jitsiConnection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED, handleDisconnected);
      jitsiConnection.connect(
        recorderCredentials
          ? {
              id: recorderCredentials.xmppJid,
              password: recorderCredentials.xmppPassword,
            }
          : {},
      );
    }).finally(() => {
      connectionPromise = null;
    });

    return connectionPromise;
  }

  async function ensureConferenceForActiveRoom(): Promise<void> {
    const desiredRoomName = buildJitsiRoomName(conferenceSlug.value, conferenceState.value.currentRoom.slug);
    appendMediaRuntimeDebugEvent('ensure-conference-for-active-room:start', {
      desiredRoomName,
    });

    if (conference.value && activeRoomName.value === desiredRoomName) {
      return;
    }

    await ensureConnection();

    if (!connection.value) {
      throw new Error('Media connection is not available.');
    }

    if (conference.value) {
      await leaveConference();
    }

    const nextConference = connection.value.initJitsiConference(desiredRoomName, jitsiConferenceOptions);
    activeRoomName.value = desiredRoomName;
    const isCurrentConference = () => conference.value === nextConference;

    nextConference.on(JitsiMeetJS.events.conference.TRACK_ADDED, ((...args: unknown[]) => {
      if (!isCurrentConference()) {
        return;
      }

      const [track] = args as [JitsiRenderableTrack];
      appendMediaRuntimeDebugEvent('conference-track-added', getTrackDebugDetail(track));
      updateTrackCollections(track);
    }) as () => unknown);
    nextConference.on(JitsiMeetJS.events.conference.TRACK_REMOVED, ((...args: unknown[]) => {
      if (!isCurrentConference()) {
        return;
      }

      const [track] = args as [JitsiRenderableTrack];
      appendMediaRuntimeDebugEvent('conference-track-removed', getTrackDebugDetail(track));
      removeTrack(track);
    }) as () => unknown);
    nextConference.on(JitsiMeetJS.events.conference.DOMINANT_SPEAKER_CHANGED, ((...args: unknown[]) => {
      if (!isCurrentConference()) {
        return;
      }

      const [participantId] = args as [string];
      dominantSpeakerId.value = participantId;
      remoteTracks.value = remoteTracks.value.map((track, index) => buildTrackView(track.track, index, dominantSpeakerId.value));
    }) as () => unknown);
    nextConference.on(JitsiMeetJS.events.conference.TRACK_AUDIO_LEVEL_CHANGED, ((...args: unknown[]) => {
      if (!isCurrentConference()) {
        return;
      }

      const [participantId, audioLevel] = args as [string, number];
      handleTrackAudioLevelChanged(participantId, audioLevel);
    }) as () => unknown);
    nextConference.on(JitsiMeetJS.events.conference.USER_JOINED, ((...args: unknown[]) => {
      if (!isCurrentConference()) {
        return;
      }

      const [, participant] = args as [string, JitsiParticipant];
      registerParticipantMappingFromParticipant(participant);
    }) as () => unknown);
    nextConference.on(JitsiMeetJS.events.conference.USER_LEFT, ((...args: unknown[]) => {
      if (!isCurrentConference()) {
        return;
      }

      const [participantId] = args as [string];
      handleParticipantLeft(participantId);
    }) as () => unknown);
    nextConference.on(JitsiMeetJS.events.conference.PARTICIPANT_PROPERTY_CHANGED, ((...args: unknown[]) => {
      if (!isCurrentConference()) {
        return;
      }

      const [participant, propertyName, , propertyValue] = args as [
        JitsiParticipant,
        string,
        string | undefined,
        string | undefined,
      ];

      if (propertyName !== CONVEX_PARTICIPANT_ID_PROPERTY || !propertyValue) {
        return;
      }

      mapJitsiParticipantToConferenceParticipant(
        participant.getId(),
        propertyValue as ConferenceState['participants'][number]['id'],
      );
    }) as () => unknown);
    nextConference.on(JitsiMeetJS.events.conference.RECORDER_STATE_CHANGED, ((...args: unknown[]) => {
      if (!isCurrentConference()) {
        return;
      }

      const [session] = args as [JibriSession];
      activeRecordingSession.value = session;

      void syncProviderRecordingState(session).finally(() => {
        const nextState = normalizeRecordingState(session);

        if (nextState === 'off') {
          activeRecordingSession.value = null;
        }
      });
    }) as () => unknown);
    nextConference.on(JitsiMeetJS.events.conference.CONFERENCE_JOINED, async () => {
      if (!isCurrentConference()) {
        return;
      }

      appendMediaRuntimeDebugEvent('conference-joined', {
        currentRoomSlug: conferenceState.value.currentRoom.slug,
        desiredRoomName,
      });
      mapJitsiParticipantToConferenceParticipant(nextConference.myUserId(), conferenceState.value.viewer.id);
      nextConference.setLocalParticipantProperty(CONVEX_PARTICIPANT_ID_PROPERTY, conferenceState.value.viewer.id);

      for (const participant of nextConference.getParticipants()) {
        registerParticipantMappingFromParticipant(participant);
      }

      clearRuntimeError();
      setStatus(`Joined ${conferenceState.value.currentRoom.displayName}.`);
      await syncTracksToIntent();
    });
    nextConference.on(JitsiMeetJS.events.conference.CONFERENCE_LEFT, () => {
      if (!isCurrentConference()) {
        return;
      }

      setStatus('Left the media room.');
      appendMediaRuntimeDebugEvent('conference-left', {
        desiredRoomName,
      });
    });
    nextConference.on(JitsiMeetJS.events.conference.CONFERENCE_FAILED, (...args: unknown[]) => {
      if (!isCurrentConference()) {
        return;
      }

      const details = args
        .map((entry) => (entry instanceof Error ? entry.message : typeof entry === 'string' ? entry : ''))
        .filter(Boolean)
        .join(' ');
      rememberRuntimeError(details ? `Media room failed to join. ${details}` : 'Media room failed to join.');
      appendMediaRuntimeDebugEvent('conference-failed', {
        details,
        desiredRoomName,
      });
      void handleControllerDeparture('Recording controller lost the media room connection while recording was active.');
    });
    nextConference.on(JitsiMeetJS.events.conference.TALK_WHILE_MUTED, () => {
      if (!isCurrentConference()) {
        return;
      }

      setStatus('You are muted. Enable your microphone if you want to speak.');
    });
    nextConference.on(JitsiMeetJS.events.conference.NOISY_MIC, () => {
      if (!isCurrentConference()) {
        return;
      }

      setStatus('Your microphone sounds noisy. Try selecting a different device.');
    });
    nextConference.on(JitsiMeetJS.events.conference.NO_AUDIO_INPUT, () => {
      if (!isCurrentConference()) {
        return;
      }

      setStatus('The selected microphone has no signal.');
    });

    nextConference.setReceiverConstraints({
      defaultConstraints: { maxHeight: 180 },
      lastN: 5,
    });
    nextConference.setReceiverVideoConstraint(180);
    await nextConference.setSenderVideoConstraint(180);

    conference.value = nextConference;
    setStatus(`Joining ${conferenceState.value.currentRoom.displayName}.`);
    nextConference.join('');
  }

  function findExistingLocalTrack(intent: TrackIntent): JitsiLocalTrack | null {
    return (
      (localTracks.value.find((trackEntry) => {
        const track = trackEntry.track as JitsiLocalTrack;
        const type = track.getType();
        const videoType = track.getVideoType();

        if (intent === 'audio') {
          return type === 'audio';
        }

        if (intent === 'camera') {
          return type === 'video' && videoType !== 'desktop';
        }

        return type === 'video' && videoType === 'desktop';
      })?.track as JitsiLocalTrack | undefined) ?? null
    );
  }

  async function createTrack(intent: TrackIntent): Promise<JitsiLocalTrack | null> {
    try {
      appendMediaRuntimeDebugEvent('create-track:start', {
        intent,
        selectedAudioInput: selectedAudioInput.value,
        selectedCamera: selectedCamera.value,
      });

      const createTracks = (usePreferredDevice: boolean) =>
        JitsiMeetJS.createLocalTracks({
          cameraDeviceId:
            intent === 'camera' && usePreferredDevice && !selectedCamera.value.startsWith('fallback-')
              ? selectedCamera.value
              : undefined,
          devices: [intent === 'camera' ? 'video' : intent === 'screenshare' ? 'desktop' : intent],
          micDeviceId:
            intent === 'audio' && usePreferredDevice && !selectedAudioInput.value.startsWith('fallback-')
              ? selectedAudioInput.value
              : undefined,
        });

      let createdTracks;

      try {
        createdTracks = await createTracks(true);
      } catch (error) {
        if ((intent === 'audio' || intent === 'camera') && isConstraintError(error)) {
          createdTracks = await createTracks(false);
        } else {
          throw error;
        }
      }

      const localTrack = createdTracks[0] as JitsiLocalTrack | undefined;

      if (!localTrack) {
        return null;
      }

      if (intent === 'screenshare') {
        localTrack.addEventListener(JitsiMeetJS.events.track.LOCAL_TRACK_STOPPED, () => {
          appendMediaRuntimeDebugEvent('local-screenshare-stopped', getTrackDebugDetail(localTrack));

          if (disposingTracks.has(localTrack)) {
            return;
          }

          void stopLocalScreenshareTrack(localTrack).catch((error) => {
            appendMediaRuntimeDebugEvent('local-screenshare-stopped:error', {
              message: error instanceof Error ? error.message : 'Unable to stop screenshare.',
            });
          });
        });
      }

      browserPermissionDenied.value = {
        ...browserPermissionDenied.value,
        audio: intent === 'audio' ? false : browserPermissionDenied.value.audio,
        camera: intent === 'camera' ? false : browserPermissionDenied.value.camera,
      };
      deniedTrackActivationIntents.delete(intent);

      updateTrackCollections(localTrack);
      appendMediaRuntimeDebugEvent('create-track:success', {
        intent,
        track: getTrackDebugDetail(localTrack),
      });

      if (conference.value) {
        await conference.value.addTrack(localTrack);
        appendMediaRuntimeDebugEvent('create-track:published', {
          intent,
          track: getTrackDebugDetail(localTrack),
        });
      }

      return localTrack;
    } catch (error) {
      const permissionDenied = isPermissionDeniedError(error);

      if (permissionDenied) {
        deniedTrackActivationIntents.add(intent);
      } else {
        deniedTrackActivationIntents.delete(intent);
      }

      if (permissionDenied && (intent === 'audio' || intent === 'camera')) {
        const permissionKey = intent === 'audio' ? 'audio' : 'camera';
        browserPermissionDenied.value = {
          ...browserPermissionDenied.value,
          [permissionKey]: true,
        };
      }

      const message = describeTrackStartupError(intent, error);
      rememberRuntimeError(message);
      appendMediaRuntimeDebugEvent('create-track:error', {
        intent,
        message,
      });
      return null;
    }
  }

  async function syncTracksToIntent(): Promise<void> {
    const desiredTrackState = desiredViewerTrackState.value;
    appendMediaRuntimeDebugEvent('sync-tracks-to-intent:start', {
      desiredTrackState,
      localTrackIds: localTracks.value.map((track) => track.id),
    });
    const desiredIntents: TrackIntent[] = [];

    if (desiredTrackState.audio) {
      desiredIntents.push('audio');
    }

    if (desiredTrackState.camera) {
      desiredIntents.push('camera');
    }

    for (const intent of ['audio', 'camera'] as const) {
      const existingTrack = findExistingLocalTrack(intent);
      const shouldExist = desiredIntents.includes(intent);

      if (!shouldExist && existingTrack) {
        appendMediaRuntimeDebugEvent('sync-tracks-to-intent:dispose-existing', {
          intent,
          track: getTrackDebugDetail(existingTrack),
        });
        await disposeTrack(existingTrack);
      }

      if (shouldExist && !existingTrack) {
        appendMediaRuntimeDebugEvent('sync-tracks-to-intent:create-missing', {
          intent,
        });
        const createdTrack = await createTrack(intent);

        if (!createdTrack && desiredViewerTrackState.value[intent] && deniedTrackActivationIntents.has(intent)) {
          deniedTrackActivationIntents.delete(intent);
          await cancelFailedTrackActivation(intent);
        }

        continue;
      }

      if (shouldExist && existingTrack) {
        appendMediaRuntimeDebugEvent('sync-tracks-to-intent:publish-existing', {
          intent,
          track: getTrackDebugDetail(existingTrack),
        });
        await ensureLocalTrackPublished(existingTrack);
      }
    }
  }

  async function syncTrackState(track: TrackIntent, value: boolean): Promise<void> {
    await setTrackState({
      conferenceSlug: conferenceSlug.value,
      participantId: conferenceState.value.viewer.id,
      track,
      value,
    });
  }

  async function cancelFailedTrackActivation(track: TrackIntent): Promise<void> {
    localTrackStateOverrides.value = {
      ...localTrackStateOverrides.value,
      [track]: false,
    };

    appendMediaRuntimeDebugEvent('track-activation-cancelled', { track });
    await syncTrackState(track, false);
  }

  function enqueueRoomSync(): void {
    roomQueue = roomQueue
      .then(async () => {
        try {
          await ensureConferenceForActiveRoom();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unable to join the media room.';
          rememberRuntimeError(message);
        }
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Unable to join the media room.';
        rememberRuntimeError(message);
      });
  }

  function enqueueTrackSync(): void {
    syncQueue = syncQueue
      .then(async () => {
        try {
          await syncTracksToIntent();
          await applyAudioOutputSelection();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unable to apply media changes.';
          rememberRuntimeError(message);
        }
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Unable to apply media changes.';
        rememberRuntimeError(message);
      });
  }

  async function updateTrackIntent(track: TrackIntent, value: boolean): Promise<void> {
    clearRuntimeError();

    if (value) {
      deniedTrackActivationIntents.delete(track);

      if (track === 'audio' || track === 'camera') {
        const permissionKey = track === 'audio' ? 'audio' : 'camera';
        browserPermissionDenied.value = {
          ...browserPermissionDenied.value,
          [permissionKey]: false,
        };
      }
    }

    if (track === 'camera' && value && findExistingLocalTrack('screenshare')) {
      await setScreenshareActive(false);
    }

    appendMediaRuntimeDebugEvent('update-track-intent:start', {
      nextValue: value,
      previousValue: desiredViewerTrackState.value[track],
      track,
    });
    localTrackStateOverrides.value = {
      ...localTrackStateOverrides.value,
      [track]: value,
    };

    enqueueTrackSync();

    try {
      await syncTrackState(track, value);

      if (value && localTrackStateOverrides.value[track] === false) {
        await syncTrackState(track, false);
        appendMediaRuntimeDebugEvent('update-track-intent:cancelled-after-ack', {
          track,
        });
        return;
      }

      appendMediaRuntimeDebugEvent('update-track-intent:ack', {
        nextValue: value,
        track,
      });
    } catch (error) {
      localTrackStateOverrides.value = {
        ...localTrackStateOverrides.value,
        [track]: conferenceState.value.viewer.trackState[track],
      };
      enqueueTrackSync();
      appendMediaRuntimeDebugEvent('update-track-intent:error', {
        message: error instanceof Error ? error.message : 'Unable to update media intent.',
        nextValue: value,
        track,
      });
      throw error;
    }
  }

  async function setScreenshareActive(value: boolean): Promise<void> {
    clearRuntimeError();

    if (value) {
      deniedTrackActivationIntents.delete('screenshare');
    }

    const existingTrack = findExistingLocalTrack('screenshare');

    if (!value) {
      if (existingTrack) {
        await disposeTrack(existingTrack);
      }

      if (conferenceState.value.viewer.trackState.screenshare) {
        await syncTrackState('screenshare', false);
      }

      return;
    }

    if (existingTrack) {
      if (!conferenceState.value.viewer.trackState.screenshare) {
        await syncTrackState('screenshare', true);
      }

      return;
    }

    if (desiredViewerTrackState.value.camera || findExistingLocalTrack('camera')) {
      localTrackStateOverrides.value = {
        ...localTrackStateOverrides.value,
        camera: false,
      };

      const cameraTrack = findExistingLocalTrack('camera');

      if (cameraTrack) {
        await disposeTrack(cameraTrack);
      }

      if (conferenceState.value.viewer.trackState.camera) {
        await syncTrackState('camera', false);
      }
    }

    const createdTrack = await createTrack('screenshare');

    if (!createdTrack) {
      if (conferenceState.value.viewer.trackState.screenshare) {
        await syncTrackState('screenshare', false);
      }

      return;
    }

    await syncTrackState('screenshare', true);
  }

  const canToggleAudio = computed(
    () => conferenceState.value.viewer.role === 'teacher' || conferenceState.value.viewer.permissions.audio,
  );
  const canToggleCamera = computed(
    () => conferenceState.value.viewer.role === 'teacher' || conferenceState.value.viewer.permissions.video,
  );

  const selectedAudioInputLabel = computed(() => resolveDeviceLabel(audioInputOptions.value, selectedAudioInput.value));
  const selectedCameraLabel = computed(() => resolveDeviceLabel(cameraOptions.value, selectedCamera.value));
  const selectedAudioOutputLabel = computed(() => resolveDeviceLabel(audioOutputOptions.value, selectedAudioOutput.value));

  const audioTracks = computed(() =>
    [...localTracks.value, ...remoteTracks.value].filter((track) => track.kind === 'audio' && !isTrackMuted(track.track)),
  );
  const videoTracks = computed(() => {
    const tracks = [...remoteTracks.value, ...localTracks.value].filter(
      (track) => track.kind === 'video' && !isTrackMuted(track.track),
    );

    return tracks.sort((left, right) => Number(right.label === 'Active speaker') - Number(left.label === 'Active speaker'));
  });
  const screenShareTrack = computed(
    () =>
      [...remoteTracks.value, ...localTracks.value].find((track) => track.kind === 'screenshare' && !isTrackMuted(track.track)) ??
      null,
  );
  const localScreenShareTrack = computed(() => localTracks.value.find((track) => track.kind === 'screenshare') ?? null);

  onMounted(() => {
    if (isMediaRuntimeDebugEnabled()) {
      window.__conferenceMediaRuntimeDebug = {
        enabled: true,
        events: [],
      };
      appendMediaRuntimeDebugEvent('media-runtime-mounted', {
        conferenceSlug: conferenceSlug.value,
      });
    }

    void loadDeviceOptions();
    navigator.mediaDevices?.addEventListener?.('devicechange', loadDeviceOptions);

    window.addEventListener('beforeunload', handleBeforeUnload);
  });

  onBeforeUnmount(() => {
    navigator.mediaDevices?.removeEventListener?.('devicechange', loadDeviceOptions);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    void disconnect();
  });

  watch(
    [audioInputOptions, cameraOptions, audioOutputOptions],
    () => {
      syncSelection(selectedAudioInput, audioInputOptions.value, fallbackDeviceOptions.audioinput[0].value);
      syncSelection(selectedCamera, cameraOptions.value, fallbackDeviceOptions.videoinput[0].value);
      syncSelection(selectedAudioOutput, audioOutputOptions.value, fallbackDeviceOptions.audiooutput[0].value);
    },
    { immediate: true },
  );

  watch(
    () => conferenceState.value.currentRoom.slug,
    () => {
      resetSpeakingState();
      enqueueRoomSync();
    },
    { immediate: true },
  );

  watch(
    () => conferenceState.value.viewer.trackState,
    (serverTrackState) => {
      const nextOverrides = { ...localTrackStateOverrides.value };
      let changed = false;

      for (const key of ['audio', 'camera', 'screenshare'] as const) {
        if (nextOverrides[key] === undefined) {
          continue;
        }

        if (nextOverrides[key] === serverTrackState[key]) {
          delete nextOverrides[key];
          changed = true;
        }
      }

      if (changed) {
        localTrackStateOverrides.value = nextOverrides;
      }
    },
    { deep: true, immediate: true },
  );

  watch(
    () => [
      desiredViewerTrackState.value.audio,
      desiredViewerTrackState.value.camera,
      selectedAudioInput.value,
      selectedCamera.value,
      selectedAudioOutput.value,
    ],
    () => {
      enqueueTrackSync();
    },
    { immediate: true },
  );

  watch(
    () => [localScreenShareTrack.value, conferenceState.value.viewer.trackState.screenshare] as const,
    ([localTrack, isReportedActive]) => {
      if (!localTrack && isReportedActive) {
        void syncTrackState('screenshare', false);
      }
    },
    { immediate: true },
  );

  watch(
    canToggleAudio,
    (allowed) => {
      if (!allowed && conferenceState.value.viewer.trackState.audio) {
        void updateTrackIntent('audio', false);
      }
    },
    { immediate: true },
  );

  watch(
    canToggleCamera,
    (allowed) => {
      if (!allowed && conferenceState.value.viewer.trackState.camera) {
        void updateTrackIntent('camera', false);
      }
    },
    { immediate: true },
  );

  return {
    audioInputOptions,
    audioTracks,
    browserPermissionDenied,
    canToggleAudio,
    canToggleCamera,
    cameraOptions,
    isRecordingStatePending,
    refreshDeviceOptions: loadDeviceOptions,
    recordingCommandInFlight,
    recordingError: computed(() => recordingError.value ?? recordingQueryError.value?.message ?? null),
    recordingState,
    runtimeError,
    runtimeStatus,
    localScreenShareTrack,
    screenShareTrack,
    speakingParticipantIds,
    selectedAudioInput,
    selectedAudioInputLabel,
    selectedAudioOutput,
    selectedAudioOutputLabel,
    selectedCamera,
    selectedCameraLabel,
    setScreenshareActive,
    selfVideoEnabled,
    toggleRecording,
    updateTrackIntent,
    videoPanelLayout,
    videoPanelVisible,
    videoTracks,
    audioOutputOptions,
  };
}
